import { TBlockchain } from 'src/types';
import { axiosInstance, DefaultTxLimit } from '.';
import {
  PaginatedModel,
  QueryParams,
  Transfer,
  BitcoinAddressSummaryStats,
  BitcoinTransactionItem,
} from '../interfaces';
import { evm } from './evm';
import { BitcoinTransaction } from 'src/components/Address/Bitcoin/TxHistoryTable/types';
import { MongoLong } from '../utils/number';

const baseEndpoint = '/blockchain';

const getTransactionByHashBatch = async (
  hash: string,
): Promise<PaginatedModel<Transfer>> => {
  const data = {
    type: 'transaction_hash',
    params: {
      transaction_hash: hash,
    },
    options: {
      limit: DefaultTxLimit,
    },
  };

  const response = await axiosInstance.post(
    `${baseEndpoint}/get_bitcoin_transfers`,
    data,
  );
  return response.data;
};

export interface BitcoinSingleTransactionResponse {
  data: BitcoinTransaction;
}
export const getTransactionByHash = async (
  hash: string | undefined,
): Promise<BitcoinSingleTransactionResponse | null> => {
  if (!hash) {
    return null;
  }
  const response = await axiosInstance.get(`/bitcoin/tx/${hash}`);
  return response.data;
};

const getBitcoinAttributions = async (addresses: string[]) => {
  const data = {
    type: 'address',
    params: {
      addresses,
      chain: 'bitcoin',
    },
    options: {
      limit: 10000,
    },
  };
  const response = await axiosInstance.post(
    `attributions/get_attributions`,
    data,
  );
  return response.data;
};

const getAccountSummary = async (addresses: string[]) => {
  // doesn't require auth
  const data = {
    type: 'address',
    params: {
      addresses,
    },
    options: {
      limit: 1,
    },
  };
  const response = await axiosInstance.post(
    `${baseEndpoint}/get_bitcoin_address_summary`,
    data,
  );
  return response.data;
};

const getBitcoinAddressSummaryStats = async (
  address: string,
): Promise<BitcoinAddressSummaryStats> => {
  const data = {
    type: 'address',
    params: {
      address,
    },
  };
  const response = await axiosInstance.post(
    `${baseEndpoint}/get_bitcoin_address_stats`,
    data,
  );
  return response.data;
};

const getBitcoinTransfers = async (
  address: string,
  filters?: QueryParams | null,
): Promise<PaginatedModel<Transfer>> => {
  // TODO: Implement filters
  const data = {
    type: 'address',
    params: {
      address,
    },
    options: {
      limit: DefaultTxLimit,
      ...filters,
    },
  };
  const response = await axiosInstance.post(
    `${baseEndpoint}/get_bitcoin_transfers`,
    data,
  );
  return response.data;
};

const getOutputSpent = async (address: string, curr_block: number) => {
  const data = {
    type: 'address',
    params: {
      address,
      curr_block,
    },
  };
  const response = await axiosInstance.post(
    `${baseEndpoint}/get_output_spent`,
    data,
  );
  return response.data;
};

const getAddressNotes = async (address: string) => {
  const endpoint = `/blockchain/get_address_notes`;
  const res = await axiosInstance.post(endpoint, { address });
  return res.data;
};

const saveAddressNotes = async (address: string, notes: string) => {
  const endpoint = `/blockchain/save_address_notes`;
  const res = await axiosInstance.post(endpoint, { address, notes });
  return res.data;
};

const getTxByHashBatch = async (txHashes: string[]) => {
  const endpoint = `/blockchain/get_tx_by_hash_batch`;
  const res = await axiosInstance.post(endpoint, { batch: txHashes });
  return res.data;
};

const getOutputsSpentBatch = async (
  outputTxHashes: { addr: string; intxid: string }[],
) => {
  const endpoint = `/blockchain/get_outputs_spent_batch`;
  const res = await axiosInstance.post(endpoint, { batch: outputTxHashes });
  return res.data;
};

const findRoot = async (address: string): Promise<string> => {
  try {
    const endpoint = `/blockchain/find_root`;
    const res = await axiosInstance.post(endpoint, { address });
    return res.data.root;
  } catch (error) {
    console.error(error);
    return '';
  }
};

const getEVMTxByHash = async (chain: TBlockchain, hash: string) => {
  const endpoint = `/${chain}/tx/${hash}`;
  const res = await axiosInstance.get(endpoint);
  return res.data;
};

const getEvmERC20TransfersByHash = async (chain: TBlockchain, hash: string) => {
  const endpoint = `/${chain}/tx/${hash}/erc20`;
  const res = await axiosInstance.get(endpoint);
  return res.data;
};

export const getBitcoinAddressTransactions = async (
  address: string,
  page: number = 1,
  sort: 'newest' | 'oldest' = 'newest',
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    sort,
  });

  const response = await axiosInstance.get(
    `/bitcoin/address/${address}/transactions?${params}`,
  );
  return response.data;
};

interface BitcoinTransactionResponse {
  txid: string;
  offset: number;
  limit: number;
  total: number;
  items: BitcoinTransactionItem[];
}

export const getBitcoinTransactionInputsOutputs = async (
  txHash: string,
  type: 'inputs' | 'outputs',
  offset: number = 0,
  limit: number = 25,
): Promise<BitcoinTransactionResponse> => {
  const response = await axiosInstance.get(
    `/bitcoin/tx/${txHash}/${type}?offset=${offset}&limit=${limit}`,
  );
  return response.data;
};

export interface EVMBalanceItem {
  _id: string;
  snapshot_date: string;
  wallet_address: string;
  token_address: string;
  raw_balance: number | MongoLong;
  total_incoming: number | MongoLong;
  total_outgoing: number | MongoLong;
  total_number_of_transactions: number;
  symbol: string;
  name: string;
  price_usd: number;
  total_value_usd: number;
  token_value_usd: number;
  actual_balance: number;
  decimals?: number;
  logo?: string;
  firstSeen?: string | null;
}

export interface EVMTokensResponse {
  tokens: EVMBalanceItem[];
  price_percentage_breakdown: {
    symbol: string;
    percentage: number;
    total_value_usd: number;
    price_usd: number | null;
  }[];
}

const getEVMNativeBalance = async (
  chain: string,
  address: string,
): Promise<{ data: EVMBalanceItem[]; error: null | string }> => {
  const response = await axiosInstance.get(
    `/${chain}/address/${address}/balance/native`,
  );
  return response.data;
};

const getEVMTokensBalance = async (
  chain: TBlockchain,
  address: string,
): Promise<{ data: EVMTokensResponse; error: null | string }> => {
  const response = await axiosInstance.get(
    `/${chain}/address/${address}/balance/tokens`,
  );
  return response.data;
};

export const blockchain = {
  evm,
  getEVMTxByHash,
  getEvmERC20TransfersByHash,
  getTransactionByHash,
  getTransactionByHashBatch,
  getBitcoinAttributions,
  getAccountSummary,
  getBitcoinTransfers,
  getOutputSpent,
  findRoot,
  getAddressNotes,
  getOutputsSpentBatch,
  getTxByHashBatch,
  saveAddressNotes,
  getBitcoinAddressSummaryStats,
  getBitcoinAddressTransactions,
  getBitcoinTransactionInputsOutputs,
  getEVMNativeBalance,
  getEVMTokensBalance,
};
